import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useInterval } from '@vueuse/core'

/**
 * 视频通话状态
 */
export const VIDEO_CALL_STATUS = {
  IDLE: 'idle', // 空闲（无通话）
  CALLING: 'calling', // 呼叫中（等待接听）
  CONNECTED: 'connected', // 已接通（通话中）
  MATCHING: 'matching', // 随机匹配中
}

/**
 * 视频通话失败原因
 */
export const VIDEO_CALL_FAILURE_REASON = {
  BE_BLOCKED: 'be_blocked', // 被拉黑
  BLOCKED: 'blocked', // 拉黑
  PERMISSION_DENIED: 'permission_denied', // 权限被拒绝
  API_ERROR: 'api_error', // API 错误
  NETWORK_ERROR: 'network_error', // 网络错误
  REFUSED: 'refused', // 被拒绝
  TIMEOUT: 'timeout', // 超时
  UNKNOWN: 'unknown', // 未知错误
  // Agora 轨道创建错误
  NOT_SUPPORTED: 'not_supported', // 浏览器不支持
  DEVICE_NOT_FOUND: 'device_not_found', // 找不到设备
  MEDIA_OPTION_INVALID: 'media_option_invalid', // 采集参数无效
  CONSTRAINT_NOT_SATISFIED: 'constraint_not_satisfied', // 采集选项不满足
}

/**
 * 权限类型
 */
export const PERMISSION_TYPE = {
  CAMERA: 'camera', // 摄像头
  MICROPHONE: 'microphone', // 麦克风
}

export const VIDEO_CALL_ROLE = {
  CALLER: 'caller', // 拨打方
  RECEIVER: 'receiver', // 接听方
}

export const useVideoCallStore = defineStore('videoCall', () => {
  // State
  const callStatus = ref(VIDEO_CALL_STATUS.IDLE)
  const currentRoomInfo = ref(null)
  const role = ref(null) // VIDEO_CALL_ROLE.CALLER | VIDEO_CALL_ROLE.RECEIVER | null

  // 直播间消息列表（仅内存存储，不持久化）
  const messages = ref([])

  // 通话时长计时器（秒）
  const {
    counter: callDuration,
    resume: resumeTimer,
    pause: pauseTimer,
    reset: resetTimer,
  } = useInterval(1000, {
    immediate: false, // 不立即开始
    controls: true,
  })

  // Getters
  const isIdle = computed(() => callStatus.value === VIDEO_CALL_STATUS.IDLE)
  const isCalling = computed(() => callStatus.value === VIDEO_CALL_STATUS.CALLING)
  const isConnected = computed(() => callStatus.value === VIDEO_CALL_STATUS.CONNECTED)
  const isMatching = computed(() => callStatus.value === VIDEO_CALL_STATUS.MATCHING)

  /**
   * 当前用户是否为拨打方
   */
  const isCaller = computed(() => role.value === VIDEO_CALL_ROLE.CALLER)

  /**
   * 当前用户是否为接听方
   */
  const isReceiver = computed(() => role.value === VIDEO_CALL_ROLE.RECEIVER)

  /**
   * 检查是否为重复邀请（基于 timestamp）
   * @param {number} timestamp - 邀请消息的时间戳
   * @param {number} dedupeWindow - 去重时间窗口（毫秒），默认 30 秒
   * @returns {boolean} - true=重复邀请，false=新邀请
   */
  function isDuplicateInvite(timestamp, dedupeWindow = 30 * 1000) {
    if (!currentRoomInfo.value) return false

    const currentTimestamp = currentRoomInfo.value.timestamp
    if (!currentTimestamp) return false

    const timeDiff = Math.abs(timestamp - currentTimestamp)

    if (timeDiff < dedupeWindow) {
      return true
    }

    return false
  }

  // Actions
  /**
   * 开始呼叫（IDLE → CALLING）
   * @param {Object} roomInfo - 房间信息
   * @param {string} callRole - 角色：'caller'（拨打方）或 'receiver'（接听方）
   */
  function startCall(roomInfo, callRole = VIDEO_CALL_ROLE.CALLER) {
    callStatus.value = VIDEO_CALL_STATUS.CALLING
    currentRoomInfo.value = roomInfo
    role.value = callRole // 设置角色
    // 重置计时器
    resetTimer()
  }

  /**
   * 接通通话（CALLING → CONNECTED）
   */
  function connectCall() {
    callStatus.value = VIDEO_CALL_STATUS.CONNECTED
    // 开始计时
    resumeTimer()
  }

  /**
   * 重置通话（* → IDLE）
   * 用于所有通话结束场景：挂断、拒绝、超时、失败等
   */
  function resetCall() {
    callStatus.value = VIDEO_CALL_STATUS.IDLE
    currentRoomInfo.value = null
    role.value = null // 重置角色
    clearMessages()
    // 停止并重置计时器
    pauseTimer()
  }

  /**
   * 添加直播间消息到列表
   * @param {Object} newMessages - 消息对象
   */
  function addMessages(newMessages) {
    if (Array.isArray(newMessages)) {
      messages.value.push(...newMessages)
      return
    }
    messages.value.push(newMessages)
  }

  /**
   * 清空直播间消息列表
   */
  function clearMessages() {
    messages.value = []
  }

  /**
   * 开始随机匹配（IDLE → MATCHING）
   */
  function startMatching() {
    callStatus.value = VIDEO_CALL_STATUS.MATCHING
  }

  return {
    // State
    callStatus,
    currentRoomInfo,
    role, // 当前用户角色：VIDEO_CALL_ROLE.CALLER | VIDEO_CALL_ROLE.RECEIVER | null
    messages,
    callDuration, // 通话时长（秒）

    // Getters
    isIdle,
    isCalling,
    isConnected,
    isMatching,
    isCaller,
    isReceiver,

    // Actions
    startCall,
    connectCall,
    resetCall,
    isDuplicateInvite,

    // 随机匹配 Actions
    startMatching,

    // 直播间消息 Actions
    addMessages,
    clearMessages,
  }
})
